import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { authApi } from '../services/authApi';
import { useLang } from '../i18n/context';

const EMPTY_PROFILE = {
  recipientName: '',
  phone: '',
  province: '',
  city: '',
  district: '',
  addressDetail: '',
  isComplete: false,
};

const PROFILE_FIELDS = [
  'recipientName',
  'phone',
  'province',
  'city',
  'district',
  'addressDetail',
];

function ShippingProfileForm({ token }) {
  const { t } = useLang();
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadedToken, setLoadedToken] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const requestSequence = useRef(0);
  const generation = useRef(0);

  const loadProfile = useCallback(async () => {
    const requestId = ++requestSequence.current;
    setLoading(true);
    setLoadFailed(false);
    setFieldErrors({});

    try {
      const result = await authApi.getShippingProfile(token);
      if (requestId === requestSequence.current) {
        setProfile({ ...EMPTY_PROFILE, ...(result.profile || {}) });
        setLoadedToken(token);
      }
    } catch (_error) {
      if (requestId === requestSequence.current) {
        setLoadFailed(true);
        setLoadedToken(token);
      }
    } finally {
      if (requestId === requestSequence.current) {
        setLoading(false);
      }
    }
  }, [token]);

  useLayoutEffect(() => {
    generation.current += 1;
    return () => {
      generation.current += 1;
      requestSequence.current += 1;
    };
  }, [token]);

  useEffect(() => {
    setSaving(false);
    setFieldErrors({});
    loadProfile();
  }, [loadProfile]);

  const change = (field) => (value) => {
    setProfile((current) => ({ ...current, [field]: value, isComplete: false }));
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const save = async () => {
    const saveGeneration = generation.current;
    const fields = {
      recipientName: profile.recipientName.trim(),
      phone: profile.phone.trim(),
      province: profile.province.trim(),
      city: profile.city.trim(),
      district: profile.district.trim(),
      addressDetail: profile.addressDetail.trim(),
    };

    if (fields.phone && !/^1[3-9]\d{9}$/.test(fields.phone)) {
      setFieldErrors((current) => ({ ...current, phone: t('profile.shipping.invalidPhone') }));
      return;
    }

    setFieldErrors({});
    setSaving(true);
    try {
      const result = await authApi.saveShippingProfile(token, fields);
      if (saveGeneration !== generation.current) return;
      setProfile({ ...EMPTY_PROFILE, ...(result.profile || {}) });
      Swal.fire({
        icon: 'success',
        title: t('profile.shipping.saveSuccess'),
        background: '#1e222d',
        color: '#d1d4dc',
        confirmButtonColor: '#bfa14a',
        timer: 1500,
        timerProgressBar: true,
      });
    } catch (error) {
      if (saveGeneration !== generation.current) return;
      const validationErrors = error?.payload?.errors || {};
      const nextFieldErrors = PROFILE_FIELDS.reduce((errors, field) => {
        const serverError = validationErrors[field];
        const message = Array.isArray(serverError) ? serverError[0] : serverError;
        if (typeof message === 'string' && message) {
          errors[field] = message;
        }
        return errors;
      }, {});
      setFieldErrors(nextFieldErrors);
      Swal.fire({
        icon: 'error',
        title: t('profile.shipping.saveFailed'),
        text: error?.message || t('profile.retryLater'),
        background: '#1e222d',
        color: '#d1d4dc',
        confirmButtonColor: '#ef5350',
        confirmButtonText: t('profile.confirm'),
      });
    } finally {
      if (saveGeneration === generation.current) {
        setSaving(false);
      }
    }
  };

  if (loading || loadedToken !== token) {
    return <section className="user-profile-card">{t('profile.shipping.loading')}</section>;
  }

  if (loadFailed) {
    return (
      <section className="user-profile-card" role="alert">
        <p>{t('profile.shipping.loadFailed')}</p>
        <button type="button" className="user-profile-save" onClick={loadProfile}>
          {t('profile.shipping.retry')}
        </button>
      </section>
    );
  }

  return (
    <section className="user-profile-card">
      <div className="user-profile-shipping-header">
        <div>
          <h2 id="shipping-profile-title">{t('profile.shipping.title')}</h2>
          <p>{t('profile.shipping.description')}</p>
        </div>
        <span className={`user-profile-shipping-status${profile.isComplete ? ' is-complete' : ''}`}>
          {t(profile.isComplete ? 'profile.shipping.complete' : 'profile.shipping.incomplete')}
        </span>
      </div>

      <form aria-labelledby="shipping-profile-title" onSubmit={(event) => { event.preventDefault(); save(); }}>
        <fieldset
          className="user-profile-fields"
          disabled={saving}
        >
          <ShippingField id="shipping-recipient" name="recipientName" label={t('profile.shipping.recipientName')} value={profile.recipientName} onChange={change('recipientName')} maxLength={50} placeholder={t('profile.shipping.recipientNamePlaceholder')} autoComplete="name" error={fieldErrors.recipientName} />
          <ShippingField id="shipping-phone" name="phone" label={t('profile.shipping.phone')} value={profile.phone} onChange={change('phone')} maxLength={11} placeholder={t('profile.shipping.phonePlaceholder')} type="tel" inputMode="numeric" autoComplete="tel" error={fieldErrors.phone} />
          <ShippingField id="shipping-province" name="province" label={t('profile.shipping.province')} value={profile.province} onChange={change('province')} maxLength={50} placeholder={t('profile.shipping.provincePlaceholder')} autoComplete="address-level1" error={fieldErrors.province} />
          <ShippingField id="shipping-city" name="city" label={t('profile.shipping.city')} value={profile.city} onChange={change('city')} maxLength={50} placeholder={t('profile.shipping.cityPlaceholder')} autoComplete="address-level2" error={fieldErrors.city} />
          <ShippingField id="shipping-district" name="district" label={t('profile.shipping.district')} value={profile.district} onChange={change('district')} maxLength={50} placeholder={t('profile.shipping.districtPlaceholder')} autoComplete="address-level3" error={fieldErrors.district} />
          <ShippingField id="shipping-address" name="addressDetail" label={t('profile.shipping.addressDetail')} value={profile.addressDetail} onChange={change('addressDetail')} maxLength={255} placeholder={t('profile.shipping.addressDetailPlaceholder')} autoComplete="street-address" error={fieldErrors.addressDetail} wide />
        </fieldset>

        <div className="user-profile-actions">
          <button type="submit" className="user-profile-save" disabled={saving}>
            {t(saving ? 'profile.shipping.saving' : 'profile.shipping.save')}
          </button>
        </div>
      </form>
    </section>
  );
}

function ShippingField({ id, name, label, value, onChange, maxLength, placeholder, error, wide = false, type = 'text', inputMode, autoComplete }) {
  return (
    <div className={`user-profile-field${wide ? ' user-profile-field--wide' : ''}`}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        name={name}
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={maxLength}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {error && <small id={`${id}-error`} className="user-profile-field-error" role="alert">{error}</small>}
    </div>
  );
}

export default ShippingProfileForm;
